import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import {
  requestLocationPermission,
  isGPSAvailable,
  getLocationPermissionState,
} from '../../services/location.service';
import LocationPermissionHelp from '../work-orders/gps/LocationPermissionHelp';
import {
  isFieldTeamRole,
  getLocationPromptDismissedKey,
  getLocationPromptEnabledKey,
  LOCATION_PROMPT_SESSION_KEY,
} from '../../constants/location-tracking-prompt';

const STATUS_LABELS = {
  granted: { text: 'Enabled', className: 'text-green-700 bg-green-50 border-green-200' },
  denied: { text: 'Blocked', className: 'text-amber-800 bg-amber-50 border-amber-200' },
  prompt: { text: 'Not enabled yet', className: 'text-gray-700 bg-gray-50 border-gray-200' },
  unknown: { text: 'Unknown', className: 'text-gray-700 bg-gray-50 border-gray-200' },
  unsupported: { text: 'Not supported on this device', className: 'text-gray-600 bg-gray-50 border-gray-200' },
};

/**
 * User Settings section for job location sharing (field team roles).
 */
function LocationTrackingSettingsSection({ user }) {
  const [permissionState, setPermissionState] = useState('unknown');
  const [isEnabling, setIsEnabling] = useState(false);
  const [showPermissionHelp, setShowPermissionHelp] = useState(false);
  const [isDismissedPermanently, setIsDismissedPermanently] = useState(false);

  const refreshPermissionState = useCallback(async () => {
    const state = await getLocationPermissionState();
    setPermissionState(state);
    if (state === 'granted' && user?.id) {
      localStorage.setItem(getLocationPromptEnabledKey(user.id), 'true');
    }
    if (user?.id) {
      setIsDismissedPermanently(localStorage.getItem(getLocationPromptDismissedKey(user.id)) === 'true');
    }
  }, [user?.id]);

  useEffect(() => {
    refreshPermissionState();
  }, [refreshPermissionState]);

  const handleEnableLocation = async () => {
    if (!isGPSAvailable()) {
      toast.error('Location is not supported on this device or browser.');
      return;
    }
    setIsEnabling(true);
    try {
      const granted = await requestLocationPermission();
      sessionStorage.setItem(LOCATION_PROMPT_SESSION_KEY, 'true');
      if (granted && user?.id) {
        localStorage.setItem(getLocationPromptEnabledKey(user.id), 'true');
        localStorage.removeItem(getLocationPromptDismissedKey(user.id));
        toast.success('Location access enabled');
      } else {
        toast.error('Location access was not granted. See instructions below.');
        setShowPermissionHelp(true);
      }
      await refreshPermissionState();
    } finally {
      setIsEnabling(false);
    }
  };

  const handleShowLoginPromptAgain = () => {
    if (!user?.id) {
      return;
    }
    localStorage.removeItem(getLocationPromptDismissedKey(user.id));
    sessionStorage.removeItem(LOCATION_PROMPT_SESSION_KEY);
    setIsDismissedPermanently(false);
    toast.success('You will see the location reminder again after your next login.');
  };

  if (!user) {
    return null;
  }

  const isFieldRole = isFieldTeamRole(user.role);

  const statusConfig = STATUS_LABELS[permissionState] || STATUS_LABELS.unknown;
  const isGranted = permissionState === 'granted';
  const isDenied = permissionState === 'denied';
  const isUnsupported = permissionState === 'unsupported' || !isGPSAvailable();

  return (
    <>
      <div className="mb-6 bg-white shadow-sm rounded-lg overflow-hidden border border-gray-200">
        <div className="px-4 py-5 sm:px-6 bg-gray-50 border-b border-gray-200">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h3 className="text-lg font-medium leading-6 text-gray-900">Job location sharing</h3>
              <p className="mt-1 text-sm text-gray-500">
                Used when you update job status at a property or company office
              </p>
            </div>
            <span
              className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold ${statusConfig.className}`}
            >
              {statusConfig.text}
            </span>
          </div>
        </div>
        <div className="p-6 space-y-4">
          {!isFieldRole && (
            <div className="rounded-md border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-600">
              This setting applies to field technicians who update jobs on site. You can still enable
              location here to test or if you perform field work.
            </div>
          )}
          <div className="rounded-md border border-sky-100 bg-sky-50 p-4 text-sm text-sky-900 space-y-2">
            <p>
              Sweetgrass uses your phone&apos;s location <strong>only</strong> when you update a job
              while you are at a rental property you are working on or at a company office.
            </p>
            <p>
              We <strong>do not</strong> track your location at home, while driving between sites, or
              anywhere else.
            </p>
            <p className="text-sky-800">
              This helps our team improve scheduling and reduce unnecessary drive time.
            </p>
          </div>

          {isDenied && (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              Location is blocked in your browser or phone settings. Use the instructions below to
              allow access, then tap Enable location again.
            </div>
          )}

          {isUnsupported && (
            <div className="rounded-md border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-600">
              This browser or device does not support location services. Use the mobile app or a
              supported browser on your phone for job check-ins.
            </div>
          )}

          <div className="flex flex-col sm:flex-row flex-wrap gap-3">
            {!isGranted && !isUnsupported && (
              <button
                type="button"
                onClick={handleEnableLocation}
                disabled={isEnabling}
                className="inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-sky-600 hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500 disabled:opacity-50"
              >
                {isEnabling ? 'Requesting…' : 'Enable location'}
              </button>
            )}
            {isGranted && (
              <p className="text-sm text-green-700 flex items-center">
                <svg className="h-5 w-5 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Location is enabled for this browser. You can change this anytime in your phone settings.
              </p>
            )}
            <button
              type="button"
              onClick={() => setShowPermissionHelp(true)}
              className="inline-flex justify-center items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500"
            >
              How to enable on your phone
            </button>
          </div>

          {isDismissedPermanently && (
            <p className="text-xs text-gray-500">
              You chose not to see the login reminder.{' '}
              <button
                type="button"
                onClick={handleShowLoginPromptAgain}
                className="text-sky-600 hover:text-sky-800 underline font-medium"
              >
                Show reminder on next login
              </button>
            </p>
          )}
        </div>
      </div>
      <LocationPermissionHelp
        isOpen={showPermissionHelp}
        onClose={() => {
          setShowPermissionHelp(false);
          refreshPermissionState();
        }}
      />
    </>
  );
}

export default LocationTrackingSettingsSection;
